import type {
    Content,
    ContentUser,
} from "@amzn/ads-discovery-theme-service-typescript-client";

import { FilterToCategoriesMapping } from "../../config/categories.config";
import type { CardDetail } from "../../types/card";
import {
    MarketingNarrativeContentOwners,
    MarketingNarrativesFilterField,
    MarketingNarrativesLastModifiedDate,
    MarketingNarrativesTotalDownloads,
    MarketingNarrativesTotalViews,
} from "../../types/filters/marketingNarratives";
import { getCategoryIds, getCategoryMap } from "../../utils/categories.utils";

export enum MarketingNarrativeDetailsType {
    CARD = "CARD",
    SHEET = "SHEET",
}

const CardFields = [
    {
        label: "Marketplace",
        value: "",
        lineClamp: 1,
        field: MarketingNarrativesFilterField.MARKETPLACE,
    },
    {
        label: "Language",
        value: "",
        lineClamp: 1,
        field: MarketingNarrativesFilterField.LANGUAGE,
    },
    {
        label: "Last modified date",
        value: "",
        lineClamp: 1,
        field: MarketingNarrativesLastModifiedDate,
    },
];

const SheetFields = [
    ...CardFields,
    {
        label: "Views",
        value: "",
        lineClamp: 1,
        field: MarketingNarrativesTotalViews,
    },
    {
        label: "Downloads",
        value: "",
        lineClamp: 1,
        field: MarketingNarrativesTotalDownloads,
    },
    {
        label: "Vertical",
        value: "",
        lineClamp: 1,
        field: MarketingNarrativesFilterField.VERTICAL,
    },
    {
        label: "Subvertical",
        value: "",
        lineClamp: 1,
        field: MarketingNarrativesFilterField.SUBVERTICAL,
    },
    {
        label: "Ad Activation Channel",
        value: "",
        lineClamp: 1,
        field: MarketingNarrativesFilterField.AD_ACTIVATION_CHANNEL,
    },
    {
        label: "Ad Products and Formats",
        value: "",
        lineClamp: 1,
        field: MarketingNarrativesFilterField.AD_PRODUCTS_AND_FORMATS,
    },
    {
        label: "Primary Content Creator Team",
        value: "",
        lineClamp: 1,
        field: MarketingNarrativesFilterField.PRIMARY_CONTENT_CREATOR_TEAM,
    },
    {
        label: "Marketing Strategy",
        value: "",
        lineClamp: 1,
        field: MarketingNarrativesFilterField.MARKETING_STRATEGY,
    },
    {
        label: "Seasonal Events",
        value: "",
        lineClamp: 1,
        field: MarketingNarrativesFilterField.SEASONAL_EVENTS,
    },
    {
        label: "Advertiser Goal",
        value: "",
        lineClamp: 1,
        field: MarketingNarrativesFilterField.ADVERTISER_GOAL,
    },
    {
        label: "Owner(s)",
        values: "",
        lineClamp: 3,
        field: MarketingNarrativeContentOwners,
    },
];

export const getMarketingNarrativeAIDetails = (
    content: Content,
    showAIFields: boolean = false,
): CardDetail[] => {
    if (!showAIFields) return [];

    const aiDetails: CardDetail[] = [];

    const aiTitle = content.aiTitle;
    const aiSummary = content.aiSummary;

    if (aiTitle) {
        aiDetails.push({
            label: "AI Title",
            value: aiTitle,
            lineClamp: 1,
        });
    }

    if (aiSummary) {
        // Format bullet points to ensure they start on new lines
        const formattedSummary = aiSummary.replace(/\s*•\s*/g, "\n• ");

        aiDetails.push({
            label: "AI Generated Description",
            value: formattedSummary,
            lineClamp: 4,
            expandable: true,
            showTooltip: false,
            logExpandClick: true,
            contentId: content.contentId,
            contentType: content.contentType,
        });
    }

    return aiDetails;
};

export const getMarketingNarrativeDetails = (
    content: Content,
    contentUsers: ContentUser[],
    viewType: MarketingNarrativeDetailsType,
): CardDetail[] => {
    const contentCategoryIds = content.contentCategoryIds ?? [];

    const fieldsMap = (
        viewType === MarketingNarrativeDetailsType.SHEET
            ? SheetFields
            : CardFields
    ).reduce(
        (prev, current) => {
            const values: string[] = [];
            const categoriesForField = FilterToCategoriesMapping[current.field];
            switch (current.field) {
                case MarketingNarrativesLastModifiedDate:
                    content.lastModifiedDate &&
                        values.push(
                            new Date(
                                content.lastModifiedDate,
                            ).toLocaleDateString(),
                        );
                    break;
                case MarketingNarrativesTotalViews:
                    content.totalViews != null &&
                        values.push(String(content.totalViews));
                    break;
                case MarketingNarrativesTotalDownloads:
                    content.totalDownloads != null &&
                        values.push(String(content.totalDownloads));
                    break;
                case MarketingNarrativeContentOwners:
                    if (contentUsers && contentUsers.length > 0) {
                        const owners = contentUsers.filter(
                            (user) =>
                                user.permissionType === "OWNER" && user.userId,
                        );
                        if (owners.length > 0) {
                            owners.forEach((owner) => {
                                if (owner.userId) {
                                    values.push(owner.userId);
                                }
                            });
                        }
                    } else {
                        values.push("N/A");
                    }
                    break;
                default:
                    if (categoriesForField && categoriesForField.length > 0) {
                        categoriesForField.forEach((category) => {
                            const categoryIdsToCheck: string[] =
                                getCategoryIds(category);

                            const matchedCategoryIds: string[] =
                                contentCategoryIds.filter(
                                    (categoryId: string) =>
                                        categoryIdsToCheck.includes(categoryId),
                                );

                            matchedCategoryIds.forEach((categoryId) => {
                                const categoryMap = getCategoryMap();
                                if (categoryMap[categoryId]) {
                                    values.push(
                                        categoryMap[categoryId].displayName!,
                                    );
                                }
                            });
                        });
                    }
            }

            prev[current.label] = {
                label: current.label,
                value: values.join(", "),
                lineClamp: current.lineClamp,
            };

            return prev;
        },
        {} as { [key: string]: CardDetail },
    );
    return Object.values(fieldsMap);
};
